import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Breadcrumbs, 
  Link, 
  Chip,
  Avatar,
  Divider,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { getBlogPostById, getFeaturedBlogPosts } from '../services/blogService';

const SocialButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(1),
  backgroundColor: 'rgba(0, 0, 0, 0.05)',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
  },
}));

const BlogDetailPage = () => {
  const { id } = useParams();
  const [blogPost, setBlogPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const loadBlogPost = async () => {
      try {
        setLoading(true);
        
        // Fetch the blog post by ID
        const result = await getBlogPostById(id);
        console.log('API response:', result); // Debug log
        
        // Check if the API returned a valid response with post property
        if (result && result.post) {
          // API returned the expected format with post and relatedPosts
          setBlogPost(result.post);
          setRelatedPosts(result.relatedPosts || []);
        } else if (result) {
          // API returned just the post directly
          setBlogPost(result);
          
          // Fetch related posts if not provided
          try {
            const featured = await getFeaturedBlogPosts(3);
            setRelatedPosts(featured.filter(item => item.id !== id));
          } catch (featuredErr) {
            console.error('Error loading featured posts:', featuredErr);
            setRelatedPosts([]);
          }
        } else {
          throw new Error('Invalid blog post data received');
        }
        
        setError(null);
      } catch (err) {
        console.error('Error loading blog post:', err);
        setError('Failed to load the blog post. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadBlogPost();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress size={isMobile ? 36 : 40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 12, md: 15 }, mb: { xs: 6, md: 10 } }}>
        <Alert severity="error" sx={{ 
          mb: 5,
          '& .MuiAlert-message': {
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }
        }}>{error}</Alert>
        <Button 
          startIcon={<ArrowBackIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />} 
          variant="outlined" 
          component={RouterLink} 
          to="/blog"
          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
        >
          Back to Blog
        </Button>
      </Container>
    );
  }

  if (!blogPost) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 12, md: 15 }, mb: { xs: 6, md: 10 } }}>
        <Alert severity="warning" sx={{ 
          mb: 5,
          '& .MuiAlert-message': {
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }
        }}>Blog post not found</Alert>
        <Button 
          startIcon={<ArrowBackIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />} 
          variant="outlined" 
          component={RouterLink} 
          to="/blog"
          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
        >
          Back to Blog
        </Button>
      </Container>
    );
  }

  // Ensure tags is always an array
  const tags = blogPost?.tags ? (Array.isArray(blogPost.tags) ? blogPost.tags : []) : [];
  const author = blogPost?.author || {};
  
  // For dynamic content rendering from the database
  const renderContent = () => {
    if (!blogPost?.content) {
      return (
        <Typography paragraph sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>
          This is a sample blog post content. In a real application, this would be rich content stored in the database and formatted properly.
          The content might include paragraphs, images, headings, quotes, and other formatted text.
        </Typography>
      );
    }
    
    if (typeof blogPost.content === 'string') {
      // Try to parse JSON string if it's stringified JSON
      try {
        const parsedContent = JSON.parse(blogPost.content);
        if (Array.isArray(parsedContent)) {
          return renderContentBlocks(parsedContent);
        }
      } catch (e) {
        // Not JSON, render as plain text
        return <Typography paragraph sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>{blogPost.content}</Typography>;
      }
      
      return <Typography paragraph sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>{blogPost.content}</Typography>;
    }
    
    // If content is already an array of blocks (for rich text content)
    if (Array.isArray(blogPost.content)) {
      return renderContentBlocks(blogPost.content);
    }
    
    return null;
  };
  
  const renderContentBlocks = (blocks) => {
    return blocks.map((block, index) => {
      switch (block.type) {
        case 'paragraph':
          return <Typography key={index} paragraph sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>{block.text}</Typography>;
        case 'heading':
          return <Typography 
            key={index} 
            variant={block.level || 'h3'} 
            sx={{ 
              my: 2,
              fontSize: { 
                xs: block.level === 'h3' ? '1.3rem' : (block.level === 'h4' ? '1.1rem' : '1rem'), 
                sm: block.level === 'h3' ? '1.5rem' : (block.level === 'h4' ? '1.3rem' : '1.1rem'),
                md: block.level === 'h3' ? '1.75rem' : (block.level === 'h4' ? '1.5rem' : '1.25rem')
              }
            }}
          >
            {block.text}
          </Typography>;
        case 'image':
          return (
            <Box key={index} sx={{ my: 4 }}>
              <img src={block.src} alt={block.alt || ''} style={{ width: '100%', borderRadius: 8 }} />
              {block.caption && <Typography variant="caption" display="block" textAlign="center" mt={1} sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>{block.caption}</Typography>}
            </Box>
          );
        case 'quote':
          return (
            <Box key={index} sx={{ my: 4, pl: 2, borderLeft: 4, borderColor: 'primary.main', py: 1 }}>
              <Typography variant="body1" fontStyle="italic" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>{block.text}</Typography>
              {block.author && <Typography variant="subtitle2" mt={1} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>— {block.author}</Typography>}
            </Box>
          );
        default:
          return null;
      }
    });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: { xs: 3, md: 5 }, mt: { xs: 10, md: 12 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3, '& .MuiBreadcrumbs-li': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}>
          <Link component={RouterLink} to="/" underline="hover" color="inherit" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Home
          </Link>
          <Link component={RouterLink} to="/blog" underline="hover" color="inherit" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Blog
          </Link>
          <Typography color="text.primary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Article</Typography>
        </Breadcrumbs>

        <Button 
          startIcon={<ArrowBackIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />} 
          variant="text" 
          component={RouterLink} 
          to="/blog"
          sx={{ 
            mb: { xs: 2, md: 4 }, 
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            py: { xs: 0.5, sm: 1 }
          }}
        >
          Back to all articles
        </Button>
        
        {/* Blog Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ mb: { xs: 2, md: 4 }, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {tags.map((tag, index) => (
              <Chip 
                key={index} 
                label={tag} 
                size="small" 
                onClick={() => navigate(`/blog?tag=${tag}`)}
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  height: { xs: 24, sm: 32 }
                }}
              />
            ))}
          </Box>

          <Typography 
            variant="h3" 
            component="h1" 
            fontWeight="bold"
            gutterBottom
            sx={{ 
              mb: { xs: 2, md: 3 },
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
            }}
          >
            {blogPost?.title}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 3, md: 5 } }}>
            <Avatar 
              src={author.avatar} 
              alt={author.name || 'Author'} 
              sx={{ 
                width: { xs: 40, md: 50 }, 
                height: { xs: 40, md: 50 }, 
                mr: 2 
              }} 
            />
            <Box>
              <Typography 
                variant="subtitle1" 
                fontWeight="bold"
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
              >
                {author.name || 'Unknown Author'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}
              >
                {typeof blogPost?.date === 'string' 
                  ? new Date(blogPost.date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })
                  : new Date(blogPost?.date || Date.now()).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })
                } · {blogPost?.readTime || '5 min read'}
              </Typography>
            </Box>
          </Box>
        </motion.div>
        
        {/* Featured Image */}
        <Box 
          component={motion.div}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          sx={{ width: '100%', height: { xs: 200, sm: 300, md: 500 }, borderRadius: 2, overflow: 'hidden', mb: { xs: 3, md: 5 } }}
        >
          <img
            src={blogPost?.image}
            alt={blogPost?.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
        
        {/* Article Content */}
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Box sx={{ 
                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }, 
                lineHeight: 1.8 
              }}>
                {renderContent()}
              </Box>
              
              {/* Tags */}
              <Box sx={{ mt: { xs: 4, md: 6 }, mb: { xs: 3, md: 4 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {tags.map((tag, index) => (
                    <Chip 
                      key={index} 
                      label={tag} 
                      onClick={() => navigate(`/blog?tag=${tag}`)}
                      sx={{ 
                        cursor: 'pointer',
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        height: { xs: 28, sm: 32 }
                      }}
                    />
                  ))}
                </Box>
              </Box>
              
              {/* Author */}
              <Box sx={{ 
                mb: { xs: 4, md: 6 }, 
                p: { xs: 2, md: 3 }, 
                bgcolor: 'background.paper', 
                borderRadius: 2, 
                boxShadow: 1 
              }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  About the author
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar 
                    src={author.avatar} 
                    alt={author.name || 'Author'} 
                    sx={{ 
                      width: { xs: 50, md: 60 }, 
                      height: { xs: 50, md: 60 }, 
                      mr: 2 
                    }} 
                  />
                  <Box>
                    <Typography 
                      variant="subtitle1" 
                      fontWeight="bold"
                      sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                    >
                      {author.name || 'Unknown Author'}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}
                    >
                      Content Writer & Travel Enthusiast
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' } }}>
                  Passionate about travel and helping others discover amazing destinations. With years of experience exploring different cultures and places, I share insights to make your trips unforgettable.
                </Typography>
              </Box>
            </motion.div>
          </Grid>
          
          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              position: { xs: 'static', md: 'sticky' }, 
              top: 100, 
              bgcolor: 'background.paper', 
              borderRadius: 2, 
              p: { xs: 2, md: 3 }, 
              boxShadow: 1 
            }}>
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  mb: { xs: 2, md: 3 },
                  fontSize: { xs: '1.1rem', sm: '1.25rem' }
                }}
              >
                Related Articles
              </Typography>
              
              {relatedPosts && relatedPosts.length > 0 ? (
                <Box>
                  {relatedPosts.map((post) => (
                    <Box key={post.id} sx={{ mb: 3 }}>
                      <StyledCard>
                        <CardMedia
                          component="img"
                          height={160}
                          image={post.image}
                          alt={post.title}
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/blog/${post.id}`)}
                        />
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Typography 
                            variant="subtitle1" 
                            component={RouterLink}
                            to={`/blog/${post.id}`}
                            sx={{ 
                              fontWeight: 'bold', 
                              textDecoration: 'none',
                              color: 'text.primary',
                              display: 'block',
                              mb: 1,
                              fontSize: { xs: '0.9rem', sm: '1rem' },
                              '&:hover': {
                                color: 'primary.main'
                              }
                            }}
                          >
                            {post.title}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          >
                            {typeof post.date === 'string' 
                              ? new Date(post.date).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })
                              : new Date(post.date || Date.now()).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })
                            } · {post.readTime || '5 min read'}
                          </Typography>
                        </CardContent>
                      </StyledCard>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  No related articles found.
                </Typography>
              )}
              
              <Box sx={{ mt: 4 }}>
                <Button 
                  fullWidth 
                  variant="contained" 
                  component={RouterLink}
                  to="/blog"
                  sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                >
                  View All Articles
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default BlogDetailPage; 